import { defineStore } from 'pinia';
import { computed, onScopeDispose, ref, watch } from 'vue';
import {
  COLOR_SCHEME_CSS_NAMES,
  COLOR_SCHEME_CSS_STORAGE_KEY,
  COLOR_SCHEME_STORAGE_KEY,
  colorSchemeCssVars,
  defaultColorScheme,
  normalizeColorScheme,
  resolveColorScheme,
  type ColorSchemeSettings,
} from '@/lib/colorScheme';

export type ThemeChoice = 'light' | 'dark' | 'system';

/** Те же ключи читает скрипт до первой отрисовки в index.html. */
export const THEME_STORAGE_KEY = 'yana.theme';
export { COLOR_SCHEME_STORAGE_KEY, COLOR_SCHEME_CSS_STORAGE_KEY };

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function readChoice(): ThemeChoice {
  const raw = readStorage(THEME_STORAGE_KEY);
  return raw === 'light' || raw === 'dark' ? raw : 'system';
}

function readColorScheme(): ColorSchemeSettings {
  try {
    return normalizeColorScheme(JSON.parse(readStorage(COLOR_SCHEME_STORAGE_KEY) ?? 'null'));
  } catch {
    return defaultColorScheme();
  }
}

export const useThemeStore = defineStore('theme', () => {
  const choice = ref<ThemeChoice>(readChoice());
  const colorScheme = ref(readColorScheme());
  const persistenceError = ref(false);
  const media =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;
  const systemDark = ref(media?.matches ?? false);
  const isDark = computed(() =>
    choice.value === 'system' ? systemDark.value : choice.value === 'dark',
  );
  const palette = computed(() =>
    resolveColorScheme(colorScheme.value, isDark.value ? 'dark' : 'light'),
  );

  function apply(): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.classList.toggle('dark', isDark.value);
    root.style.colorScheme = isDark.value ? 'dark' : 'light';
    // Стандартная схема возвращает исходный CSS, включая прозрачные границы.
    // Полный список очищает также переменные, поставленные скриптом до загрузки Vue.
    if (colorScheme.value.preset === 'standard') {
      for (const name of COLOR_SCHEME_CSS_NAMES) root.style.removeProperty(name);
    } else {
      for (const [name, color] of Object.entries(colorSchemeCssVars(palette.value))) {
        root.style.setProperty(name, color);
      }
    }
  }

  function persistColorScheme(): void {
    try {
      const settings = JSON.stringify(colorScheme.value);
      localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, settings);
      if (colorScheme.value.preset === 'standard') {
        localStorage.removeItem(COLOR_SCHEME_CSS_STORAGE_KEY);
      } else {
        // Обе темы готовы заранее: до первой отрисовки системный режим может
        // отличаться от того, который был при последнем сохранении.
        localStorage.setItem(
          COLOR_SCHEME_CSS_STORAGE_KEY,
          JSON.stringify({
            version: 1,
            settings,
            light: colorSchemeCssVars(resolveColorScheme(colorScheme.value, 'light')),
            dark: colorSchemeCssVars(resolveColorScheme(colorScheme.value, 'dark')),
          }),
        );
      }
      persistenceError.value = false;
    } catch {
      // При запрете хранилища настройка продолжает работать в открытой вкладке.
      persistenceError.value = true;
    }
  }

  watch([isDark, colorScheme], apply, {
    immediate: true,
    deep: true,
    flush: 'sync',
  });
  watch(
    choice,
    (value) => {
      try {
        localStorage.setItem(THEME_STORAGE_KEY, value);
      } catch {
        // Приватный режим и политики браузера могут запретить запись.
      }
    },
    { flush: 'sync' },
  );

  function set(value: ThemeChoice): void {
    choice.value = value === 'light' || value === 'dark' ? value : 'system';
  }

  function saveColorScheme(settings: ColorSchemeSettings): void {
    colorScheme.value = normalizeColorScheme(settings);
    persistColorScheme();
  }

  function resetColorScheme(): void {
    colorScheme.value = defaultColorScheme();
    try {
      localStorage.removeItem(COLOR_SCHEME_STORAGE_KEY);
      localStorage.removeItem(COLOR_SCHEME_CSS_STORAGE_KEY);
      persistenceError.value = false;
    } catch {
      persistenceError.value = true;
    }
  }

  function onSystemChange(event: MediaQueryListEvent): void {
    systemDark.value = event.matches;
  }

  function onStorage(event: StorageEvent): void {
    // sessionStorage имеет такие же ключи, но к личному оформлению не относится.
    try {
      if (event.storageArea && event.storageArea !== localStorage) return;
    } catch {
      return;
    }
    if (event.key === null || event.key === THEME_STORAGE_KEY) choice.value = readChoice();
    if (event.key === null || event.key === COLOR_SCHEME_STORAGE_KEY) {
      colorScheme.value = readColorScheme();
    }
  }

  media?.addEventListener('change', onSystemChange);
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  onScopeDispose(() => {
    media?.removeEventListener('change', onSystemChange);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  });
  // Обновляем кэш после восстановления неполной/старой настройки.
  if (readStorage(COLOR_SCHEME_STORAGE_KEY) !== null) persistColorScheme();

  return {
    choice,
    isDark,
    set,
    colorScheme,
    palette,
    persistenceError,
    saveColorScheme,
    resetColorScheme,
  };
});
